import React, { useCallback } from 'react';
import classnames from 'classnames';
import { ENotificationEventType, INotification, INotificationSearchRequest } from 'services/CrmApi/types/NotificationTypes';
import Checkbox from 'ui/Checkbox';
import MagnifyingIcon from 'ui/Icons/magnifying-glass.component.svg';
import CircleCloseIcon from 'ui/Icons/circleClose.component.svg';
import TextInput from 'pureUi/TextInput';
import SingleSelect, { ISingleSelectOption } from 'ui/SingleSelect';
import FluidButton from 'ui/FluidButton';

interface INotificationsCollectionWrapperProps {
  children: React.ReactNode;
  notifications?: INotification[];
  isLoading?: boolean;
  // filters
  searchValue?: string;
  onSearchValueChange?: (searchValue: string) => void;
  onlyShowUnread?: boolean;
  onOnlyShowUnreadChange?: (onlyUnread: boolean) => void;
  notificationType?: ENotificationEventType;
  onNotificationTypeChange?: (notificationType: ENotificationEventType) => void;
  // export
  onNotificationsExport?: () => void;
  isExportInProgress?: boolean;
}

export const NotificationsCollectionWrapper: React.FC<INotificationsCollectionWrapperProps> = ({
  notifications,
  children,
  isLoading,
  searchValue,
  onSearchValueChange,
  onlyShowUnread,
  onOnlyShowUnreadChange,
  notificationType,
  onNotificationTypeChange,
  onNotificationsExport,
  isExportInProgress,
}) => {
  const handleOnlyShowUnreadChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onOnlyShowUnreadChange && onOnlyShowUnreadChange(e.target.checked);
    },
    [onOnlyShowUnreadChange]
  );
  const handleSearchValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onSearchValueChange && onSearchValueChange(e.currentTarget.value);
    },
    [onSearchValueChange]
  );

  const handleExportNotifications = useCallback(() => {
    onNotificationsExport && onNotificationsExport();
  }, [onNotificationsExport]);

  const handleSetNotificationType = useCallback((notificationType: string) => {
    onNotificationTypeChange && onNotificationTypeChange(notificationType as ENotificationEventType);
  }, [onNotificationTypeChange]);

  const notificationTypeOptions: ISingleSelectOption[] = [
    { label: 'All types', value: undefined },
    { label: 'Booking Confirmed', value: ENotificationEventType.BOOKING_CONFIRMED },
    { label: 'Booking Requested', value: ENotificationEventType.BOOKING_REQUESTED },
    { label: 'User Login TA', value: ENotificationEventType.USER_LOGIN_TA },
    { label: 'New Basket', value: ENotificationEventType.NEW_BASKET_ITEM },
  ];
  return (
    <div className="notifications-tooltip-container p-[15px] pb-0 shadow-pe6 rounded-[4px] min-w-[420px] max-w-[420px] flex flex-col gap-[10px] max-h-[calc(100dvh-76px)]">
      {notifications ? (
        <div className="flex flex-col gap-[15px]">
          {/* notifications title and unread only checkbox */}
          <div className="1st-line flex items-center justify-between gap-[10px]">
            <p className="notifications-tooltip text-17px font-pt-sans text-black m-0">
              <span className="font-bold">Notifications</span>
              {!isLoading ? <span className="font-normal">&nbsp;({notifications?.length})</span> : null}
            </p>
            {onOnlyShowUnreadChange && (
              <label className="only-show-unread flex items-center font-pt-sans text-[16px] text-black gap-[8px]">
                <Checkbox checked={onlyShowUnread} onChange={handleOnlyShowUnreadChange} />
                Only show unread
              </label>
            )}
          </div>

          {/* search by company or contact */}
          <div className="2nd-line">
            <TextInput
              onChange={handleSearchValueChange}
              placeholder="Enter Booking Reference ..."
              className="flex flex-col relative"
              value={searchValue}
              inputClassName="!py-[10px] !px-[30px] !text-black !bg-white-hard"
            >
              <CircleCloseIcon
                onClick={() => onSearchValueChange && onSearchValueChange('')}
                className={classnames(
                  'block cursor-pointer fill-brown-80 hover:fill-brown-120 absolute w-[18px] top-[10px] right-[10px]',
                  { hidden: searchValue === '' }
                )}
              />
              <MagnifyingIcon className="absolute w-[18px] top-[10px] left-[10px] fill-light-gray rotate-90" />
            </TextInput>
          </div>

          <div className="3rd-line flex items-center justify-between gap-[10px]">
            <SingleSelect
              fieldId="notification-type"
              className="notification-type min-w-[200px] max-w-[200px]"
              labelClassName="text-[16px] leading-[21px] font-bold"
              value={notificationType}
              options={notificationTypeOptions}
              onChange={handleSetNotificationType}
              maxVisibleItems={5}
            />

            {notifications && notifications.length > 0 && (
              <FluidButton
                type="secondary"
                isLoading={isExportInProgress}
                onClick={handleExportNotifications}
                disabled={isExportInProgress}
              >
                Export
              </FluidButton>
            )}
          </div>
        </div>
      ) : null}
      {children}
    </div>
  );
};
